import { useState, useEffect } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel, Loading } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { RuleSelect } from "@/components/admin";

const METHOD_FIELDS = {
  bank: ["Bank Transfer", [["bank_name", "Bank name"], ["account_name", "Account holder"], ["account_number", "Account number"], ["ifsc", "IFSC / SWIFT"]]],
  upi: ["UPI", [["upi_id", "UPI ID"]]],
  usdt_trc20: ["USDT (TRC20)", [["address", "Wallet address"]]],
  usdt_bep20: ["USDT (BEP20)", [["address", "Wallet address"]]],
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-4 rounded-lg border border-[#1E2738] bg-[#0B0E14] px-4 py-3">
    <span className="text-sm text-slate-200">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} className="data-[state=checked]:bg-[#10B981]" />
  </label>
);

const Num = ({ label, value, onChange, testid }) => (
  <div><label className="nx-label">{label}</label><input type="number" step="0.01" min="0" className="nx-input num" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>
);

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [tiers, setTiers] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setS(data); setTiers(data.deposit_tiers.join(", ")); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!s) return <Loading />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const setRef = (k, v) => setS({ ...s, referral: { ...s.referral, [k]: v } });
  const setM = (m, k, v) => setS({ ...s, methods: { ...s.methods, [m]: { ...s.methods[m], [k]: v } } });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...s,
        welcome_bonus_amount: Number(s.welcome_bonus_amount), min_withdrawal: Number(s.min_withdrawal),
        max_withdrawal: Number(s.max_withdrawal), withdrawal_fee_percent: Number(s.withdrawal_fee_percent),
        deposit_tiers: tiers.split(",").map((t) => Number(t.trim())).filter((t) => t > 0),
        referral: { ...s.referral, deposit_value: Number(s.referral.deposit_value), trading_value: Number(s.referral.trading_value) },
      };
      delete body.id;
      delete body.min_deposit;
      delete body.deposit_review_hours;
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      setTiers(data.deposit_tiers.join(", "));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="Platform settings" subtitle="All rules take effect immediately — no code changes needed."
        actions={<button className="nx-btn-primary" onClick={save} disabled={busy} data-testid="save-settings-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save settings</button>} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Welcome bonus" testid="settings-welcome-bonus">
          <div className="space-y-4 p-5">
            <Toggle label="Credit welcome bonus to new sign-ups" checked={s.welcome_bonus_enabled} onChange={(v) => set("welcome_bonus_enabled", v)} testid="welcome-bonus-enabled-switch" />
            <Num label="Bonus amount (USD)" value={s.welcome_bonus_amount} onChange={(v) => set("welcome_bonus_amount", v)} testid="welcome-bonus-amount-input" />
            <div><label className="nx-label">Terms shown to users</label>
              <textarea rows={3} className="nx-input" value={s.welcome_bonus_terms} onChange={(e) => set("welcome_bonus_terms", e.target.value)} data-testid="welcome-bonus-terms-input" /></div>
            <p className="text-[11px] text-slate-500">Bonus is credited once per new account at registration, held as locked credit and becomes withdrawable after the user's first approved deposit.</p>
          </div>
        </Panel>
        <Panel title="Referral program (regular users)" testid="settings-referral">
          <div className="space-y-4 p-5">
            <Toggle label="Referral program enabled" checked={s.referral.enabled} onChange={(v) => setRef("enabled", v)} testid="referral-enabled-switch" />
            <div className="grid grid-cols-2 gap-4">
              <div><label className="nx-label">Deposit commission</label><RuleSelect value={s.referral.deposit_type} onChange={(v) => setRef("deposit_type", v)} testid="referral-deposit-type" /></div>
              <Num label="Value" value={s.referral.deposit_value} onChange={(v) => setRef("deposit_value", v)} testid="referral-deposit-value" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="nx-label">Trading commission</label><RuleSelect value={s.referral.trading_type} onChange={(v) => setRef("trading_type", v)} testid="referral-trading-type" /></div>
              <Num label="Value" value={s.referral.trading_value} onChange={(v) => setRef("trading_value", v)} testid="referral-trading-value" />
            </div>
          </div>
        </Panel>
        <Panel title="Deposits" testid="settings-deposits">
          <div className="space-y-4 p-5">
            <div><label className="nx-label">Amount tiers (USD, comma separated)</label>
              <input className="nx-input num" value={tiers} onChange={(e) => setTiers(e.target.value)} data-testid="deposit-tiers-input" /></div>
            {Object.entries(METHOD_FIELDS).map(([m, [label, fields]]) => (
              <div key={m} className="space-y-3 rounded-xl border border-[#1E2738] p-4">
                <Toggle label={label} checked={s.methods[m]?.enabled} onChange={(v) => setM(m, "enabled", v)} testid={`method-${m}-switch`} />
                <div className="grid gap-3 sm:grid-cols-2">
                  {fields.map(([k, l]) => (
                    <div key={k} className={fields.length === 1 ? "sm:col-span-2" : ""}><label className="nx-label">{l}</label>
                      <input className="nx-input num" value={s.methods[m]?.[k] || ""} onChange={(e) => setM(m, k, e.target.value)} data-testid={`method-${m}-${k}-input`} /></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Withdrawals & returns" testid="settings-withdrawals">
          <div className="space-y-4 p-5">
            <Toggle label="Withdrawals enabled platform-wide" checked={s.withdrawals_enabled} onChange={(v) => set("withdrawals_enabled", v)} testid="withdrawals-enabled-switch" />
            <div className="grid grid-cols-3 gap-4">
              <Num label="Min (USD)" value={s.min_withdrawal} onChange={(v) => set("min_withdrawal", v)} testid="min-withdrawal-input" />
              <Num label="Max (USD)" value={s.max_withdrawal} onChange={(v) => set("max_withdrawal", v)} testid="max-withdrawal-input" />
              <Num label="Fee %" value={s.withdrawal_fee_percent} onChange={(v) => set("withdrawal_fee_percent", v)} testid="withdrawal-fee-input" />
            </div>
            <Toggle label="Automatic daily returns (00:05 UTC)" checked={s.daily_returns_enabled} onChange={(v) => set("daily_returns_enabled", v)} testid="daily-returns-enabled-switch" />
          </div>
        </Panel>
        <Panel title="Support" testid="settings-support">
          <div className="space-y-2 p-5">
            <label className="nx-label">Support email</label>
            <input type="email" required className="nx-input" value={s.support_email || ""} onChange={(e) => set("support_email", e.target.value)} data-testid="support-email-input" />
            <p className="text-[11px] text-slate-500">Shown wherever users are offered "Contact Support via Email". Updates across the site instantly.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
